'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PersonalInfo {
  name: string;
  email: string;
  phone: string;
  language: string;
}

interface PersonalInfoSectionProps {
  initialData: PersonalInfo;
}

const PersonalInfoSection = ({ initialData }: PersonalInfoSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<PersonalInfo>(initialData);
  const [errors, setErrors] = useState<Partial<PersonalInfo>>({});

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'हिंदी (Hindi)' },
    { code: 'ta', name: 'தமிழ் (Tamil)' },
    { code: 'te', name: 'తెలుగు (Telugu)' },
    { code: 'bn', name: 'বাংলা (Bengali)' },
  ];

  const validateForm = (): boolean => {
    const newErrors: Partial<PersonalInfo> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number (10 digits starting with 6-9)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      setIsEditing(false);
      // Save logic would go here
    }
  };

  const handleCancel = () => {
    setFormData(initialData);
    setErrors({});
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="UserCircleIcon" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-lg text-foreground">Personal Information</h2>
            <p className="text-sm text-muted-foreground">Manage your account details</p>
          </div>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
          >
            <Icon name="PencilIcon" size={16} />
            <span>Edit</span>
          </button>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Full Name</label>
          {isEditing ? (
            <div>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full px-4 py-2 bg-background border rounded-md text-sm focus-ring ${
                  errors.name ? 'border-error' : 'border-input'
                }`}
                placeholder="Enter your full name"
              />
              {errors.name && <p className="text-xs text-error mt-1">{errors.name}</p>}
            </div>
          ) : (
            <p className="text-sm text-foreground font-medium">{formData.name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Email Address</label>
          {isEditing ? (
            <div>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-4 py-2 bg-background border rounded-md text-sm focus-ring ${
                  errors.email ? 'border-error' : 'border-input'
                }`}
                placeholder="Enter your email"
              />
              {errors.email && <p className="text-xs text-error mt-1">{errors.email}</p>}
            </div>
          ) : (
            <p className="text-sm text-foreground font-medium">{formData.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Phone Number</label>
          {isEditing ? (
            <div>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className={`w-full px-4 py-2 bg-background border rounded-md text-sm focus-ring ${
                  errors.phone ? 'border-error' : 'border-input'
                }`}
                placeholder="Enter 10-digit mobile number"
              />
              {errors.phone && <p className="text-xs text-error mt-1">{errors.phone}</p>}
            </div>
          ) : (
            <p className="text-sm text-foreground font-medium">+91 {formData.phone}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Preferred Language</label>
          {isEditing ? (
            <select
              value={formData.language}
              onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-input rounded-md text-sm focus-ring"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-foreground font-medium">
              {languages.find((l) => l.code === formData.language)?.name}
            </p>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="flex items-center gap-3 mt-6 pt-6 border-t border-border">
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
          >
            Save Changes
          </button>
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 bg-muted text-foreground rounded-md font-medium text-sm transition-smooth hover:bg-border"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default PersonalInfoSection;